import type { PerfTier } from '../types';

const TIERS: readonly PerfTier[] = ['low', 'medium', 'high'];

/** How long between background re-probes. */
const REPROBE_INTERVAL_MS = 30_000;
/** Frames sampled per FPS probe (~1s at 60fps). */
const PROBE_FRAMES = 60;

const tierIndex = (tier: PerfTier): number => TIERS.indexOf(tier);

const demote = (tier: PerfTier, steps = 1): PerfTier =>
  TIERS[Math.max(0, tierIndex(tier) - steps)] ?? 'low';

const isTier = (value: string | null): value is PerfTier =>
  value !== null && (TIERS as readonly string[]).includes(value);

/**
 * `navigator.deviceMemory` and `navigator.connection` are non-standard and
 * missing in Firefox and Safari, so they are declared here rather than relied
 * on from lib.dom.
 */
interface NavigatorWithHints extends Navigator {
  deviceMemory?: number;
  connection?: { saveData?: boolean };
}

/** Publishes the tier on `window`, toggles the CSS classes, and notifies React. */
function apply(tier: PerfTier): void {
  const prev = window.__PERF_TIER__;
  if (prev === tier) return;

  window.__PERF_TIER__ = tier;
  document.documentElement.classList.toggle('perf-low', tier === 'low');
  document.documentElement.classList.toggle('perf-medium', tier === 'medium');
  window.dispatchEvent(new CustomEvent('perftierready', { detail: { tier } }));
}

/** Starting tier from the device's reported specs. */
function specTier(): PerfTier {
  const nav = navigator as NavigatorWithHints;
  let tier: PerfTier = 'high';

  const cores = nav.hardwareConcurrency;
  if (typeof cores === 'number' && cores <= 7) {
    // 8+ cores keeps 'high'; anything less steps down once.
    tier = demote(tier);
  }

  const mem = nav.deviceMemory;
  if (typeof mem === 'number' && mem < 8) {
    tier = demote(tier);
  }

  // A phone-sized touch device is capped at 'medium' regardless of specs.
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  if (coarse && window.innerWidth <= 768 && tierIndex(tier) > tierIndex('medium')) {
    tier = 'medium';
  }

  return tier;
}

/** Conditions that force 'low' regardless of measured performance. */
function hardOverride(): PerfTier | null {
  const nav = navigator as NavigatorWithHints;
  if (nav.connection?.saveData === true) return 'low';
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 'low';
  return null;
}

/** Measures average FPS over `PROBE_FRAMES` frames. */
function probeFps(done: (fps: number) => void): void {
  const samples: number[] = [];
  let last = performance.now();
  let frames = 0;

  const tick = (now: number): void => {
    const dt = now - last;
    last = now;
    // Skip the first interval — it includes scheduling latency.
    if (frames > 0 && dt > 0) samples.push(1000 / dt);
    frames++;

    if (frames <= PROBE_FRAMES) {
      requestAnimationFrame(tick);
      return;
    }

    if (samples.length === 0) return;
    done(samples.reduce((sum, v) => sum + v, 0) / samples.length);
  };

  requestAnimationFrame(tick);
}

/**
 * Classifies the device into a performance tier and keeps it up to date.
 *
 * Runs before React mounts. Components read the result through `usePerfTier`,
 * and the `perf-low` / `perf-medium` classes clamp CSS animation globally.
 *
 * Priority: an explicit override (`?perf=low`, remembered for the session)
 * beats a hard override (Save-Data, reduced motion), which beats the measured
 * spec-plus-FPS classification.
 */
export function initPerfDetector(): void {
  const params = new URLSearchParams(location.search);
  const urlOverride = params.get('perf');

  let override: string | null = null;

  if (isTier(urlOverride)) {
    override = urlOverride;
    try {
      sessionStorage.setItem('perfTierOverride', urlOverride);
    } catch {
      // Private browsing can reject writes; the override still holds for this page.
    }
  } else {
    try {
      override = sessionStorage.getItem('perfTierOverride');
    } catch {
      override = null;
    }
  }

  // An explicit override pins the tier — skip probing entirely.
  if (isTier(override)) {
    apply(override);
    return;
  }

  apply(hardOverride() ?? specTier());

  const runProbe = (): void => {
    if (document.hidden) return; // Backgrounded tabs throttle rAF and read as slow.

    probeFps((fps) => {
      const hard = hardOverride();
      if (hard) {
        apply(hard);
        return;
      }

      let next = specTier();
      if (fps < 40) next = 'low';
      else if (fps < 55) next = demote(next);
      apply(next);
    });
  };

  if (document.readyState === 'complete') runProbe();
  else window.addEventListener('load', runProbe, { once: true });

  setInterval(runProbe, REPROBE_INTERVAL_MS);

  window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', () => {
    apply(hardOverride() ?? specTier());
  });
}
